import { Fragment, Suspense, useMemo, useRef, useState } from 'react';
import type { Placement } from 'react-aria';
import { metricWindowingFrontend } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ErrorBoundary } from '@gonfalon/error-boundaries/ErrorBoundary';
import { CopyToClipboard } from '@gonfalon/launchpad-experimental/CopyToClipboard';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import { toHref, toMetric } from '@gonfalon/navigator/navigators';
import { useSuspenseMetric } from '@gonfalon/rest-api/hooks/useSuspenseMetric';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Alert,
  Button,
  Code,
  Focusable,
  LinkButton,
  ProgressBar,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import { MetricDefinition, metricDefinitionIncludesSuccessCriteria } from '../MetricDefinition';
import { MetricEventDefault } from '../MetricEventDefault';
import { HealthIndicator } from '../MetricHealthCheck';
import type { MetricKind } from '../MetricKind';
import { MetricLabel } from '../MetricLabel';
import { MetricLastSeenInfo } from '../MetricLastSeenInfo';
import { MetricMeasurementWindow } from '../MetricMeasurementWindow';
import { MetricSuccessCriteria } from '../MetricSuccessCriteria';
import { hasWinsorization, MetricWinsorization } from '../MetricWinsorization';
import { getRatioAggLabel } from '../RatioMetricDefinitionBox';
import type { Metric } from '../types';

import styles from './MetricInfoPopover.module.css';

interface MetricInfoPopoverBaseProps {
  children: React.ReactNode;
  placement?: Placement;
  metricVersionId?: string;
  truncate?: boolean;
  /** Override the route project key when metric details live in a different project. */
  projectKey?: string;
  /**
   * Optional list of randomization units that are actually in use by the experiment or guarded rollout.
   * When provided, only these randomization units will be shown in the metric definition,
   * rather than all randomization units the metric supports.
   */
  randomizationUnitsInUse?: string[];
}

type MetricInfoPopoverProps = MetricInfoPopoverBaseProps &
  (
    | { metric: Metric; metricKey?: never }
    | { metric?: never; metricKey: string }
    | { metric: Metric; metricKey: string }
  );

function getRatioTermNoun(unitAggType: string | undefined, isNumeric: boolean | undefined): 'events' | 'values' | null {
  if (unitAggType === 'count_distinct') {
    return null;
  }
  if (isNumeric) {
    return 'values';
  }
  return 'events';
}

function RatioUnitsList({ units }: { units: string[] }) {
  if (units.length === 0) {
    return null;
  }
  return (
    <>
      {units.map((unit, index) => (
        <Fragment key={unit}>
          {index > 0 && (index === units.length - 1 ? ' or ' : ', ')}
          <Code size="small">{unit}</Code>
        </Fragment>
      ))}
    </>
  );
}

function RatioMetricDefinitionSentence({
  metric,
  randomizationUnitsInUse,
}: {
  metric: Metric;
  randomizationUnitsInUse?: string[];
}) {
  const denom = metric.denominator;
  if (!denom) {
    return null;
  }

  const metricUnits = metric.analysisUnits?.length ? metric.analysisUnits : (metric.randomizationUnits ?? []);
  const filtered = randomizationUnitsInUse
    ? metricUnits.filter((u) => randomizationUnitsInUse.includes(u))
    : metricUnits;
  const units = filtered.length > 0 ? filtered : metricUnits;

  const numeratorAgg = getRatioAggLabel(metric.unitAggregationType, metric.isNumeric);
  const denominatorAgg = getRatioAggLabel(denom.unitAggregationType, denom.isNumeric);

  const numeratorNoun = getRatioTermNoun(metric.unitAggregationType, metric.isNumeric);
  const denominatorNoun = getRatioTermNoun(denom.unitAggregationType, denom.isNumeric);

  // unitAggregationField exists on the API response for ratio metrics but is not
  // yet reflected in the generated MetricRep OpenAPI types — access it safely.
  const numeratorColumn = (metric as Metric & { unitAggregationField?: string }).unitAggregationField;
  const denominatorColumn = denom.unitAggregationField;

  const numeratorTerm = numeratorNoun ? (
    <>{`numerator ${numeratorNoun}`}</>
  ) : (
    <>
      {'numerator '}
      {numeratorColumn && <Code size="small">{numeratorColumn}</Code>}
    </>
  );

  const denominatorTerm = denominatorNoun ? (
    <>{`denominator ${denominatorNoun}`}</>
  ) : (
    <>
      {'denominator '}
      {denominatorColumn && <Code size="small">{denominatorColumn}</Code>}
    </>
  );

  return (
    <span className={styles.ratioDefinitionSentence} id="metricDefinition">
      <Code size="small">{numeratorAgg}</Code> {numeratorTerm}
      {units.length > 0 && (
        <>
          {' '}
          per <RatioUnitsList units={units} />
        </>
      )}
      {' and '}
      <Code size="small">{denominatorAgg}</Code> {denominatorTerm}
      {units.length > 0 && (
        <>
          {' '}
          per <RatioUnitsList units={units} />
        </>
      )}
      , then compute numerator total divided by denominator total.
    </span>
  );
}

function LastSeenAndLink({ metric, projectKey }: { metric: Metric; projectKey: string }) {
  return (
    <div className={styles.lastSeenAndLink}>
      <MetricLastSeenInfo metric={metric} hideTooltip />
      <LinkButton
        target="_blank"
        variant="minimal"
        size="small"
        href={toHref(toMetric({ projectKey, metricKey: metric.key }))}
      >
        <Icon name="link-external" size="tiny" />
        View details
      </LinkButton>
    </div>
  );
}

function MetricPopoverField({ icon, label, children }: { icon: IconName; label: string; children: React.ReactNode }) {
  return (
    <div className={styles.metricFieldRow}>
      <div className={styles.metricFieldLabel}>
        <Icon name={icon} size="tiny" aria-hidden="true" />
        <span>{label}</span>
      </div>
      <div className={styles.metricFieldValue}>{children}</div>
    </div>
  );
}

const metricPopoverDisplaySchema = z
  .object({
    isRatioMetric: z.boolean(),
    hasEventDefault: z.boolean(),
    hasSuccessCriteria: z.boolean(),
    hasUnit: z.boolean(),
    hasWindow: z.boolean(),
    hasRatioCustomWindow: z.boolean(),
    hasWinsor: z.boolean(),
  })
  .transform((state) => ({
    ...state,
    showNonRatioMetricFields:
      !state.isRatioMetric &&
      (state.hasEventDefault || state.hasSuccessCriteria || state.hasUnit || state.hasWindow || state.hasWinsor),
  }));

type MetricPopoverDisplayState = z.infer<typeof metricPopoverDisplaySchema>;

function deriveMetricPopoverDisplayState(metric: Metric): MetricPopoverDisplayState {
  const isRatioMetric = !!metric.denominator;
  // MetricMeasurementWindow returns null when windowStartOffset is non-zero but windowEndOffset is absent,
  // so exclude that case to avoid rendering an empty field label.
  const windowStartIsNonZeroAndNoEnd =
    metric.windowStartOffset !== undefined && metric.windowStartOffset !== 0 && metric.windowEndOffset === undefined;
  const hasSuccessCriteria =
    !isRatioMetric &&
    (!!metric.successCriteria || metric.kind === 'click' || metric.kind === 'pageview') &&
    !metricDefinitionIncludesSuccessCriteria(metric);
  const hasWindow =
    !isRatioMetric &&
    metricWindowingFrontend() &&
    (metric.windowStartOffset !== undefined || metric.windowEndOffset !== undefined) &&
    !windowStartIsNonZeroAndNoEnd;
  const hasRatioCustomWindow =
    isRatioMetric &&
    metricWindowingFrontend() &&
    (metric.windowStartOffset !== undefined ||
      metric.windowEndOffset !== undefined ||
      metric.denominator?.windowStartOffset !== undefined ||
      metric.denominator?.windowEndOffset !== undefined);

  return metricPopoverDisplaySchema.parse({
    isRatioMetric,
    hasEventDefault: !!metric.eventDefault,
    hasSuccessCriteria,
    hasUnit: !!metric.unit,
    hasWindow,
    hasRatioCustomWindow,
    hasWinsor: hasWinsorization(metric),
  });
}

const MetricInfoPopoverContent: React.FC<{
  metric: Metric;
  projectKey: string;
  randomizationUnitsInUse?: string[];
}> = ({ metric, projectKey, randomizationUnitsInUse }) => {
  const displayState = useMemo(() => deriveMetricPopoverDisplayState(metric), [metric]);
  const { isRatioMetric, hasSuccessCriteria, hasWindow, hasRatioCustomWindow, hasWinsor, showNonRatioMetricFields } =
    displayState;

  // Grouped under the event key when there is one, otherwise shown under the definition
  // so trace metrics (filters, no event key) don't lose the indicator.
  const filtersIndicator = !!metric.filters && (
    <span className={styles.filtersApplied}>
      <Icon name="filter-list" size="tiny" aria-hidden="true" /> Event filters applied
    </span>
  );

  return (
    <div className={styles.infoContainer}>
      <div className={styles.titleRow}>
        <div className={styles.titleColumn}>
          <TooltipTrigger>
            <Focusable>
              <div className={styles.metricName} role="button" aria-label={`View details for ${metric.name}`}>
                {`${metric.name}`}
              </div>
            </Focusable>
            <Tooltip>{metric.name}</Tooltip>
          </TooltipTrigger>
          {!isRatioMetric && (
            <HealthIndicator
              kind="icon"
              metric={{
                kind: metric.kind,
                eventKey: metric?.eventKey || '',
                dataSource: metric?.dataSource,
                _versionId: metric?._versionId,
                randomizationUnits: metric?.randomizationUnits || [],
                isNumeric: metric.isNumeric || false,
                successCriteria: metric?.successCriteria || undefined,
                unitAggregationType: metric?.unitAggregationType,
              }}
            />
          )}
        </div>
        <MetricLabel
          kind={(metric.kind as MetricKind) ?? 'click'}
          isNumeric={metric.isNumeric}
          unitAggregationType={metric.unitAggregationType}
        />
      </div>
      {metric.description && <div className={styles.description}>{metric.description}</div>}

      <div className={styles.popoverBody}>
        <div className={styles.definitionSection}>
          <span className={styles.definitionLabel}>Metric definition</span>

          {isRatioMetric ? (
            <>
              <RatioMetricDefinitionSentence metric={metric} randomizationUnitsInUse={randomizationUnitsInUse} />
              {filtersIndicator}
            </>
          ) : (
            <>
              <MetricDefinition metric={metric} randomizationUnitsInUse={randomizationUnitsInUse} />
              {!metric.eventKey && filtersIndicator}
            </>
          )}

          {hasRatioCustomWindow && (
            <span className={styles.windowNote}>
              <Icon name="info-circle-outline" size="tiny" aria-hidden="true" />
              Metric uses events with custom measurement window applied
            </span>
          )}
        </div>

        {!isRatioMetric && metric.eventKey && (
          <div className={styles.eventKeySection}>
            <span className={styles.definitionLabel}>Event key</span>
            <CopyToClipboard text={metric.eventKey} tooltip="Copy event key">
              <Button
                size="small"
                data-test-id="event-key"
                aria-label={`Copy ${metric.eventKey} to your clipboard.`}
                className={styles.eventKey}
              >
                <TextTruncator>{metric.eventKey}</TextTruncator>
                <Icon name="copy-clipboard" size="small" />
              </Button>
            </CopyToClipboard>
            {filtersIndicator}
          </div>
        )}

        {showNonRatioMetricFields && (
          <div className={styles.metricFields}>
            {!!metric.eventDefault && (
              <MetricPopoverField icon="click" label={`Units without ${metric.kind === 'trace' ? 'spans' : 'events'}`}>
                <MetricEventDefault metric={metric} />
              </MetricPopoverField>
            )}
            {hasSuccessCriteria && (
              <MetricPopoverField icon="bullseye-arrow" label="Success criteria">
                <MetricSuccessCriteria metric={metric} />
              </MetricPopoverField>
            )}
            {metric.unit && (
              <MetricPopoverField icon="tools" label="Unit of measure">
                {metric.unit}
              </MetricPopoverField>
            )}
            {hasWindow && (
              <MetricPopoverField icon="clock" label="Measurement window">
                <MetricMeasurementWindow
                  windowStartOffset={metric.windowStartOffset}
                  windowEndOffset={metric.windowEndOffset}
                />
              </MetricPopoverField>
            )}
            {hasWinsor && (
              <MetricPopoverField icon="chart-histogram" label="Winsorization">
                <MetricWinsorization metric={metric} />
              </MetricPopoverField>
            )}
          </div>
        )}

        {isRatioMetric && (metric.successCriteria || metric.unit) && (
          <div className={styles.metricFieldsBordered}>
            {metric.successCriteria && (
              <MetricPopoverField icon="bullseye-arrow" label="Success criteria">
                <MetricSuccessCriteria metric={metric} />
              </MetricPopoverField>
            )}
            {metric.unit && (
              <MetricPopoverField icon="tools" label="Unit of measure">
                {metric.unit}
              </MetricPopoverField>
            )}
          </div>
        )}

        <LastSeenAndLink metric={metric} projectKey={projectKey} />
      </div>
    </div>
  );
};

const MetricInfoPopoverSuspenseMetric: React.FC<{
  projectKey: string;
  metricKey: string;
  metricVersionId?: string;
  randomizationUnitsInUse?: string[];
}> = ({ metricKey, metricVersionId, projectKey, randomizationUnitsInUse }) => {
  const { data: metric } = useSuspenseMetric({
    projectKey,
    metricKey: metricKey ?? '',
    params: { versionId: metricVersionId },
  });
  return (
    <MetricInfoPopoverContent
      metric={metric}
      projectKey={projectKey}
      randomizationUnitsInUse={randomizationUnitsInUse}
    />
  );
};

export const MetricInfoPopover: React.FC<MetricInfoPopoverProps> = ({
  children,
  metric,
  placement = 'top',
  metricKey,
  metricVersionId,
  truncate,
  randomizationUnitsInUse,
  projectKey: projectKeyOverride,
}) => {
  const routeProjectKey = useProjectKey();
  const projectKey = projectKeyOverride ?? routeProjectKey;
  const ref = useRef(null);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState<true | undefined>();

  return (
    <TooltipTrigger>
      <span className={truncate ? styles.linkWrapper : undefined} ref={ref}>
        {children}
      </span>
      <Tooltip
        isOpen={isHoveringTooltip}
        placement={placement}
        triggerRef={ref}
        className={styles.tooltip}
        variant="popover"
      >
        {/* Plain div, not a button: the copy control inside is interactive, so this wrapper
            must not be a button (no nested buttons). It only needs to track hover. */}
        <div
          style={{ all: 'unset' }}
          onMouseEnter={() => setIsHoveringTooltip(true)}
          onMouseLeave={() => setIsHoveringTooltip(undefined)}
        >
          {metric ? (
            <MetricInfoPopoverContent
              metric={metric}
              projectKey={projectKey}
              randomizationUnitsInUse={randomizationUnitsInUse}
            />
          ) : (
            <Suspense fallback={<ProgressBar aria-label="Loading…" isIndeterminate size="large" />}>
              <ErrorBoundary
                owner="team-metrics"
                severity="medium"
                fallbackRender={() => <Alert status="error">Error fetching this metric's information</Alert>}
              >
                <MetricInfoPopoverSuspenseMetric
                  metricKey={metricKey ?? ''}
                  metricVersionId={metricVersionId}
                  projectKey={projectKey}
                  randomizationUnitsInUse={randomizationUnitsInUse}
                />
              </ErrorBoundary>
            </Suspense>
          )}
        </div>
      </Tooltip>
    </TooltipTrigger>
  );
};
